/**
 * `ScenePhase` from the document, as a page reads it for the Swift
 * environment (main.ts) and `mount` reads it for a React root's
 * `useScenePhase`: `background` while the document is hidden, else `active`
 * when the window has focus and `inactive` when it does not. The browser
 * signals every change through `visibilitychange`, `focus` and `blur`.
 */

import type { ScenePhase } from './protocol';

/** What `currentScenePhase` reads: a `Document`, or a stand-in in tests. */
export interface ScenePhaseDocument {
  visibilityState: string;
  hasFocus(): boolean;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

/** Where the focus events arrive: a `Window`, or a stand-in in tests. */
export interface ScenePhaseWindow {
  addEventListener(type: 'focus' | 'blur', listener: () => void): void;
  removeEventListener(type: 'focus' | 'blur', listener: () => void): void;
}

export function currentScenePhase(doc: ScenePhaseDocument = document): ScenePhase {
  if (doc.visibilityState === 'hidden') return 'background';
  return doc.hasFocus() ? 'active' : 'inactive';
}

/**
 * Calls `onChange` with the new phase each time it changes (not with the
 * current one). Returns a function that stops watching.
 */
export function watchScenePhase(
  onChange: (phase: ScenePhase) => void,
  { doc = document, win = window }: { doc?: ScenePhaseDocument; win?: ScenePhaseWindow } = {},
): () => void {
  let last = currentScenePhase(doc);
  const check = (): void => {
    const next = currentScenePhase(doc);
    if (next === last) return;
    last = next;
    onChange(next);
  };
  doc.addEventListener('visibilitychange', check);
  win.addEventListener('focus', check);
  win.addEventListener('blur', check);
  return () => {
    doc.removeEventListener('visibilitychange', check);
    win.removeEventListener('focus', check);
    win.removeEventListener('blur', check);
  };
}
